import React, { useState, useEffect } from 'react';
import { extinguisherService, uploadService, geocodeService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { compressImage, formatFileSize, isCompressionSupported } from '../utils/imageCompression';
import ImageModal from './ImageModal';

// Geolocation error codes (W3C standard)
const GEOLOCATION_ERROR = {
  PERMISSION_DENIED: 1,
  POSITION_UNAVAILABLE: 2,
  TIMEOUT: 3
};

function ExtinguisherForm({ onSuccess }) {
  const [formData, setFormData] = useState({
    address: '',
    lat: null,
    lng: null,
    expiration: '',
    imageUrl: '',
    service_provider: '', // field for company name
    location_type: '', // field for location type
    estimated_units: '', // field for estimated number of units
  });
  const [showCustomProvider, setShowCustomProvider] = useState(false);
  const [customProvider, setCustomProvider] = useState('');
  const [showCustomLocationType, setShowCustomLocationType] = useState(false);
  const [customLocationType, setCustomLocationType] = useState('');
  const [showFullImage, setShowFullImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [originalFileSize, setOriginalFileSize] = useState(0);
  const [compressedFileSize, setCompressedFileSize] = useState(0);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      console.log('Requesting geolocation...');
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          console.log('Geolocation success:', position);
          const { latitude, longitude } = position.coords;
          
          try {
            const address = await geocodeService.reverseGeocode(latitude, longitude);
            setFormData(prev => ({
              ...prev,
              lat: latitude,
              lng: longitude,
              address: address
            }));
            setError(''); // Clear any previous error
          } catch (error) {
            console.error('Geocoding failed:', error);
            setFormData(prev => ({
              ...prev,
              lat: latitude,
              lng: longitude
            }));
            setError('Got your location but could not get address. Please enter address manually.');
          }
        },
        (error) => {
          console.error('Geolocation failed:', error);
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
          
          if (error.code === GEOLOCATION_ERROR.PERMISSION_DENIED) {
            console.error('Permission denied by the user.');
            setError('Location permission denied. Please enter address manually.');
          } else if (error.code === GEOLOCATION_ERROR.POSITION_UNAVAILABLE) {
            console.error('Position unavailable.');
            setError('Location unavailable. Please enter address manually.');
          } else if (error.code === GEOLOCATION_ERROR.TIMEOUT) {
            console.error('Geolocation request timed out.');
            setError('Location request timed out. Please enter address manually.');
          } else {
            setError('Unable to get your location. Please enter address manually.');
          }
        },
        {
          enableHighAccuracy: false, // Try with less accuracy first
          timeout: 15000, // Increase timeout
          maximumAge: 60000 // 1 minute cache
        }
      );
    } else {
      console.error('Geolocation not supported');
      setError('Geolocation is not supported by this browser. Please enter address manually.');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Check if compression is supported
    if (!isCompressionSupported()) {
      setError('Image compression is not supported in this browser');
      return;
    }

    setError('');
    setCompressing(true);
    setOriginalFileSize(file.size);

    try {
      // Compress the image
      const compressedFile = await compressImage(file, 200); // 200KB max
      
      setSelectedFile(compressedFile);
      setCompressedFileSize(compressedFile.size);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(compressedFile);
      
    } catch (error) {
      console.error('Image compression failed:', error);
      setError('Failed to compress image. Please try a different image.');
    } finally {
      setCompressing(false);
    }
  };

  const uploadImage = async () => {
    if (!selectedFile) return null;
    
    try {
      const { data } = await uploadService.getUploadUrl(
        selectedFile.name,
        selectedFile.type
      );
      
      const uploadResponse = await fetch(data.uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }
      
      return data.imageUrl;
    } catch (error) {
      console.error('Image upload failed:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Check if user is authenticated
    if (!user) {
      setError('You must be logged in to report a fire extinguisher.');
      setLoading(false);
      return;
    }

    try {
      let imageUrl = formData.imageUrl;
      
      if (selectedFile) {
        imageUrl = await uploadImage();
      }

      const submissionData = {
        ...formData,
        imageUrl,
        userId: user?.id,
        location_type: formData.location_type || null, // Convert empty string to null
        estimated_units: formData.estimated_units || null // Convert empty string to null
      };
      
      console.log('Frontend submitting data:', submissionData);
      
      await extinguisherService.create(submissionData);

      setMessage('Fire extinguisher reported successfully!');
      setFormData({
        address: formData.address,
        lat: formData.lat,
        lng: formData.lng,
        expiration: '',
        imageUrl: '',
        service_provider: '',
        location_type: '',
        estimated_units: ''
      });
      
      setShowCustomProvider(false);
      setCustomProvider('');
      setShowCustomLocationType(false);
      setCustomLocationType('');
      setSelectedFile(null);
      setImagePreview('');
      setOriginalFileSize(0);
      setCompressedFileSize(0);
      
      // Call the success callback to refresh user stats
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setError('Failed to report fire extinguisher. Please try again.');
      console.error('Submit error:', error);
    }

    setLoading(false);
  };

  const handleServiceProviderChange = (e) => {
    const value = e.target.value;
    if (value === 'Other') {
      setShowCustomProvider(true);
      setFormData({...formData, service_provider: ''});
    } else {
      setShowCustomProvider(false);
      setCustomProvider('');
      setFormData({...formData, service_provider: value});
    }
  };

  const handleCustomProviderChange = (e) => {
    const value = e.target.value;
    setCustomProvider(value);
    setFormData({...formData, service_provider: value});
  };

  const handleLocationTypeChange = (e) => {
    const value = e.target.value;
    if (value === 'Other') {
      setShowCustomLocationType(true);
      setFormData({...formData, location_type: ''});
    } else {
      setShowCustomLocationType(false);
      setCustomLocationType('');
      setFormData({...formData, location_type: value});
    }
  };

  const handleCustomLocationTypeChange = (e) => {
    const value = e.target.value;
    setCustomLocationType(value);
    setFormData({...formData, location_type: value});
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}
      
      {!user && (
        <div className="warning-message" style={{ 
          padding: '1rem', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '4px', 
          marginBottom: '1rem',
          color: '#856404'
        }}>
          ⚠️ You must be logged in to report a fire extinguisher.
        </div>
      )}
      
      <div className="form-group">
        <label htmlFor="address">Address</label>
        <input
          type="text"
          id="address"
          value={formData.address}
          onChange={(e) => setFormData({...formData, address: e.target.value})}
          required
          placeholder="Enter or wait for auto-fill..."
        />
      </div>

      <div className="form-group">
        <label htmlFor="expiration">Expiration Date</label>
        <div style={{ position: 'relative' }}>
          <input
            type="date"
            id="expiration"
            value={formData.expiration}
            onChange={(e) => {
              setFormData({...formData, expiration: e.target.value});
            }}
            min={new Date().toISOString().split('T')[0]}
            required
            style={{ 
              textAlign: 'left',
              paddingLeft: '0.75rem'
            }}
          />
        </div>
      </div>
      
      <div className="form-group">
        <label htmlFor="service_provider">Service Provider</label>
        <select
          id="service_provider"
          value={showCustomProvider ? 'Other' : formData.service_provider}
          onChange={handleServiceProviderChange}
          required
        >
          <option value=""></option>
          <option value="Jorgensen Co">Jorgensen Co</option>
          <option value="Cintas / River City">Cintas / River City</option>
          <option value="Sacramento Fire Extinguisher Co">Sacramento Fire Extinguisher Co</option>
          <option value="Butch Young">Butch Young</option>
          <option value="Marqee Fire">Marqee Fire</option>
          <option value="Western States">Western States</option>
          <option value="Hayden Fire">Hayden Fire</option>
          <option value="Parisi Fire">Parisi Fire</option>
          <option value="Sentinel Fire">Sentinel Fire</option>
          <option value="CFS">CFS</option>
          <option value="Cosco">Cosco</option>
          <option value="Foothill Fire">Foothill Fire</option>
          <option value="Johnson Controls">Johnson Controls</option>
          <option value="Other">Other (specify below)</option>
        </select>
        
        {showCustomProvider && (
          <div style={{ marginTop: '0.5rem' }}>
            <label htmlFor="custom_provider">Service Provider Name</label>
            <input
              type="text"
              id="custom_provider"
              value={customProvider}
              onChange={handleCustomProviderChange}
              placeholder="Enter provider name"
              required
              style={{ marginTop: '0.25rem' }}
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="location_type">Location Type (Optional)</label>
        <select
          id="location_type"
          value={showCustomLocationType ? 'Other' : formData.location_type}
          onChange={handleLocationTypeChange}
        >
          <option value=""></option>
          <option value="Apartment / Condo">Apartment / Condo</option>
          <option value="Church or School">Church or School</option>
          <option value="Industrial/Warehouse">Industrial/Warehouse</option>
          <option value="Office">Office</option>
          <option value="Restaurant">Restaurant</option>
          <option value="Other">Other (specify below)</option>
        </select>
        
        {showCustomLocationType && (
          <div style={{ marginTop: '0.5rem' }}>
            <label htmlFor="custom_location_type">Location Type</label>
            <input
              type="text"
              id="custom_location_type"
              value={customLocationType}
              onChange={handleCustomLocationTypeChange}
              placeholder="Enter location type"
              style={{ marginTop: '0.25rem' }}
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="estimated_units">Estimated Number of Units (Optional)</label>
        <input
          type="number"
          id="estimated_units"
          value={formData.estimated_units}
          onChange={(e) => setFormData({...formData, estimated_units: e.target.value})}
          placeholder="Enter estimated number of units"
          min="1"
          style={{ textAlign: 'left' }}
        />
      </div>

      <div className="form-group">
        <label style={{ marginBottom: '0.25rem' }}>Inspection Tag (Optional)</label>
        
        {/* File picker input */}
        <input
          type="file"
          id="image-file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={compressing}
          style={{ display: 'none' }}
        />
        
        {/* Camera input */}
        <input
          type="file"
          id="image-camera"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          disabled={compressing}
          style={{ display: 'none' }}
        />
        
        {/* Buttons */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginLeft: '0.5rem', marginTop: '0.25rem' }}>
          <button
            type="button"
            onClick={() => document.getElementById('image-camera').click()}
            disabled={compressing}
            style={{
              width: '48px',
              height: '48px',
              backgroundColor: 'transparent',
              color: compressing ? '#999' : '#6c757d',
              border: 'none',
              cursor: compressing ? 'not-allowed' : 'pointer',
              fontSize: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
            title="Take Photo"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4,4H7L9,2H15L17,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z"/>
            </svg>
          </button>
          
          <button
            type="button"
            onClick={() => document.getElementById('image-file').click()}
            disabled={compressing}
            style={{
              width: '48px',
              height: '48px',
              backgroundColor: 'transparent',
              color: compressing ? '#999' : '#6c757d',
              border: 'none',
              cursor: compressing ? 'not-allowed' : 'pointer',
              fontSize: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
            title="Choose File"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/>
            </svg>
          </button>
        </div>
        
        {compressing && (
          <div className="compression-status">
            <p>🔄 Compressing image...</p>
          </div>
        )}
        
        {imagePreview && (
          <div className="image-preview" style={{
            marginTop: '0.5rem',
            marginLeft: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <img 
              src={imagePreview} 
              alt="Preview" 
              onClick={() => setShowFullImage(true)}
              style={{
                width: '80px',
                height: '80px',
                objectFit: 'cover',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
              }}
              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              title="Click to view full size"
            />
            <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
              <div>Image ready</div>
              <small style={{ fontSize: '0.8rem', color: '#999' }}>
                {originalFileSize > compressedFileSize ? 
                  `Compressed from ${formatFileSize(originalFileSize)} to ${formatFileSize(compressedFileSize)}` :
                  `${formatFileSize(compressedFileSize)}`
                }
              </small>
            </div>
          </div>
        )}
      </div>

      {/* Separator before submit button */}
      <div style={{
        marginTop: '2rem',
        marginBottom: '1rem',
        borderTop: '1px solid #e9ecef',
        paddingTop: '1.5rem',
        textAlign: 'center'
      }}>
        <button type="submit" className="btn" disabled={loading || !formData.address || !user}>
          {loading ? 'Submitting...' : !user ? 'Login Required' : 'Report Extinguisher'}
        </button>
      </div>

      {/* Full Image Modal */}
      <ImageModal 
        isVisible={showFullImage}
        imageUrl={imagePreview}
        altText="Full size preview"
        onClose={() => setShowFullImage(false)}
      />
    </form>
  );
}

export default ExtinguisherForm;